import React from 'react';

import { Input } from '@/components/form/Input';
import { OrganizationHeader } from '@/components/OrganizationHeader';
import { useMSRPCalculator } from '@/hooks/useMSRPCalculator';
import { StepProps } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import {
  MSRPCalculatorDisplay,
  NegotiatedPriceInput,
  ScanningFrequencySelector,
  SubscriptionTierSelector,
} from '@/sections/settings/subscription/components';

/**
 * Step 4: Subscription Pricing - Configure subscription tier, frequency, limits, and pricing
 * Only appears for MANAGED and SAAS customer types
 */
export const SubscriptionPricingStep: React.FC<StepProps> = ({
  formData,
  setFormData,
  isSubmitting,
}) => {
  // Calculate MSRP based on current form data
  const { msrp, breakdown } = useMSRPCalculator({
    numberOfApplications: formData.subscription_applications || 0,
    scanSchedule: formData.subscription_scan_schedule || 'daily',
    supportTier: formData.subscription_tier || 'professional',
    numberOfAssets: formData.subscription_assets || 0,
  });

  // Update MSRP in form data when it changes
  React.useEffect(() => {
    setFormData(prev => ({ ...prev, subscription_msrp: msrp }));
  }, [msrp, setFormData]);

  return (
    <div className="flex h-[700px] flex-col">
      <OrganizationHeader
        displayName={formData.settings_display_name}
        profilePicture={formData.profile_picture}
        subtitle="Configure subscription pricing and coverage"
        className="mb-8"
      />

      <div className="flex-1 overflow-y-auto">
        <div className="space-y-8">
          {/* Subscription Tier Section */}
          <SubscriptionTierSelector
            selectedTier={formData.subscription_tier || 'professional'}
            onTierChange={tier =>
              setFormData(prev => ({ ...prev, subscription_tier: tier }))
            }
            disabled={isSubmitting}
            tiers={['foundational', 'professional', 'ultimate']} // Exclude freemium for paid customers
          />

          {/* Scanning Frequency Section */}
          <ScanningFrequencySelector
            selectedFrequency={formData.subscription_scan_schedule || 'daily'}
            onFrequencyChange={frequency =>
              setFormData(prev => ({
                ...prev,
                subscription_scan_schedule: frequency,
              }))
            }
            disabled={isSubmitting}
          />

          {/* Coverage Limits Section */}
          <section className="space-y-4">
            <h3 className="text-chariot-text-primary text-lg font-semibold">
              Coverage Limits
            </h3>
            <p className="text-chariot-text-secondary text-sm">
              Set maximum limits for assets and applications to be monitored
              under this subscription.
            </p>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-chariot-text-primary block text-sm font-medium">
                  Asset Coverage Limit
                </label>
                <Input
                  name="subscription_assets"
                  value={formData.subscription_assets?.toString() || '0'}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      subscription_assets: parseInt(e.target.value) || 0,
                    }))
                  }
                  disabled={isSubmitting}
                  placeholder="Enter asset limit"
                  className="w-full"
                />
                <p className="text-chariot-text-secondary text-xs">
                  Maximum number of assets to monitor
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-chariot-text-primary block text-sm font-medium">
                  Application Coverage Limit
                </label>
                <Input
                  name="subscription_applications"
                  value={formData.subscription_applications?.toString() || '0'}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      subscription_applications: parseInt(e.target.value) || 0,
                    }))
                  }
                  disabled={isSubmitting}
                  placeholder="Enter application limit"
                  className="w-full"
                />
                <p className="text-chariot-text-secondary text-xs">
                  Maximum number of applications/FQDNs to scan
                </p>
              </div>
            </div>
          </section>

          {/* MSRP Calculator Display */}
          <MSRPCalculatorDisplay
            msrp={msrp}
            breakdown={breakdown}
            title="Calculated MSRP"
            tooltipText="Auto-calculated based on tier, frequency, and coverage limits"
          />

          {/* Negotiated Price Section */}
          <NegotiatedPriceInput
            value={formData.subscription_cost || 0}
            onChange={value =>
              setFormData(prev => ({ ...prev, subscription_cost: value }))
            }
            msrp={msrp}
            disabled={isSubmitting}
            title="Negotiated Annual Price"
          />
        </div>
      </div>
    </div>
  );
};
